import React, { createContext, useContext, useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import useWebSocket from "react-use-websocket";
import { useApp } from "./AppProvider";
import * as T from "../types";

const SocketContext = createContext();

export type SocketProviderProps = {
  children: React.ReactNode;
  onTranscript?: (transcript: T.TranscriptionWSResponse) => void;
};

const SocketProvider: React.FC = forwardRef(({ children, onTranscript }: SocketProviderProps, ref) => {
  const { accessToken } = useApp();
  const [messageHistory, setMessageHistory] = useState<T.TranscriptionWSResponse[]>([]);
  const [connected, setConnected] = useState(false);
  const intervalRef = useRef(null);

  useImperativeHandle(ref, () => ({
    close: () => {
      getWebSocket()?.close();
    }
  }));

  const { sendMessage, sendJsonMessage, lastMessage, lastJsonMessage, readyState, getWebSocket } = useWebSocket(
    process.env.SRT_SOCKET_HOST + `?token=${accessToken}`,
    {
      onOpen: () => {
        setConnected(true);
        intervalRef.current = setInterval(() => {
          sendMessage(JSON.stringify({ messageType: "KEEP_ALIVE" }));
        }, 30 * 1_000);
      },
      onClose: () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        setConnected(false);
      },
      shouldReconnect: () => true
    }
  );

  useEffect(() => {
    if (lastJsonMessage !== null) {
      const jsonMessage = lastJsonMessage as T.TranscriptionWSResponse;
      if (typeof onTranscript === "function" && jsonMessage?.message?.messageType === "AWARE") {
        onTranscript(jsonMessage);
      }
      setMessageHistory((prev) => [lastJsonMessage, ...prev]);
    }
  }, [lastJsonMessage]);

  return (
    <SocketContext.Provider
      value={{
        connected,
        messageHistory,
        sendJsonMessage,
        sendMessage,
        lastJsonMessage,
        lastMessage,
        getWebSocket,
        readyState
      }}
    >
      {children}
    </SocketContext.Provider>
  );
});

const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export { useSocket, SocketProvider, SocketContext };
